import { TableContainer } from '@mui/material'
import { EditToolbar } from './EditToolbar'
import {
  DataGridPro,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid-pro'
import { tsStudyPhase } from '../../../Typescript/StuartPy/StudyPhase'
import GenericModal from '../../../Components/GenericModal'
import { AxiosResponse } from 'axios'
import { ArrowDownward, ArrowUpward, Delete, Edit } from '@mui/icons-material'
import { useMutation, useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'
import React, { useEffect, useState } from 'react'
import { tsStudyAttribute } from '../../../Typescript/StuartPy/StudyAttribute'
import PrepareRenderInputs from './PrepareRenderInputs'
import { attributeColsMod, scope } from './AttributeColsMod'
import { SelectAttributeOptions } from './SelectOptions'
import DeleteAttributeModal from './DeleteAttributeModal'
import { useUserContext } from '../../../Contexts/UserConfig'
import PyAPI from '../../../APIs/Python/APIs/PyAPI'
import preparePatchFields from './PreparePatchFields'
import { useAttributeColsDF } from './AttributeColsDF'

function AttributesTable({ phase }: { phase: tsStudyPhase }) {
  const { whoAmI } = useUserContext()
  const { loginConfig } = useUserContext()
  const { study_id } = useParams()
  const queryClient = useQueryClient()
  const attributeCols = useAttributeColsDF(phase)
  const [dbOrder, setDbOrder] = useState<number[]>([])
  const [newOrder, setNewOrder] = useState<number[]>([])
  const [modalErrors, setModalErrors] = useState<Record<string, any>>({})
  const [openModal, setOpenModal] = useState<scope | undefined>(undefined)
  const [modalData, setModalData] = useState<tsStudyAttribute>(
    {} as tsStudyAttribute,
  )

  const addAttribute = useMutation<any, AxiosResponse>(
    () =>
      PyAPI.put(
        whoAmI,
        `/study/${study_id}/phase/${phase.PhaseID}/attribute?data_type=${modalData.AttrDatatype!}`,
      ),
    {
      onSuccess: async (data) => {
        setModalData(data)
        setOpenModal(scope.edit)
        await queryClient.invalidateQueries({
          queryKey: ['PyAPI', '/study'],
        })
      },
    },
  )

  const editAttribute = useMutation<any, AxiosResponse>(
    () =>
      PyAPI.patch(
        whoAmI,
        `/study/${study_id}/phase/${phase.PhaseID}/attribute?attr_id=${modalData.AttrID!}`,
        preparePatchFields(attributeColsMod, modalData),
      ),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['PyAPI', '/study'],
        })
      },
    },
  )

  //No need to refresh as postion already swapped locally
  const { mutate: updateAttr } = useMutation<
    any,
    AxiosResponse,
    { attr_id: string; data: any },
    () => void
  >(({ attr_id, data }) =>
    PyAPI.patch(
      whoAmI,
      `/study/${study_id}/phase/${phase.PhaseID}/attribute?attr_id=${attr_id}`,
      data,
    ),
  )

  useEffect(() => {
    if (phase.attributes) {
      const initialOrder = phase.attributes
        .sort((a, b) => a.SeqNo - b.SeqNo)
        .map((a) => a.AttrID)
      setDbOrder(initialOrder)
      setNewOrder(initialOrder)
    }
  }, [phase.attributes])

  useEffect(() => {
    if (newOrder.length === dbOrder.length) {
      const seqChanges: any = {}
      newOrder.forEach((item, index) => {
        if (dbOrder[index] !== item) {
          seqChanges[item] = index + 1
        }
      })

      if (Object.keys(seqChanges).length === 2) {
        setDbOrder(newOrder)
        updateAttr({
          attr_id: Object.keys(seqChanges)[0],
          data: {
            SeqNo: Object.values(seqChanges)[0],
          },
        })
        updateAttr({
          attr_id: Object.keys(seqChanges)[1],
          data: {
            SeqNo: Object.values(seqChanges)[1],
          },
        })
      }
    }
  }, [newOrder, dbOrder, updateAttr])

  const handleUp = (value: number) => {
    const currentIndex = dbOrder.indexOf(value)
    const newOrder = [...dbOrder]
    newOrder.splice(currentIndex - 1, 0, value)
    newOrder.splice(currentIndex + 1, 1)
    setNewOrder(newOrder)
  }

  const handleDown = (value: number) => {
    const currentIndex = dbOrder.indexOf(value)
    const newOrder = [...dbOrder]
    newOrder.splice(currentIndex + 2, 0, value)
    newOrder.splice(currentIndex, 1)
    setNewOrder(newOrder)
  }

  const [deleteModal, setDeleteAttribute] = useState<number | null>(null)

  return (
    <>
      <GenericModal
        title={`${openModal === 'add' ? 'Add New' : 'Edit'} Attribute`}
        open={!!openModal}
        setClose={() => {
          setOpenModal(undefined)
          if (openModal === 'add') {
            addAttribute.reset()
          } else {
            editAttribute.reset()
          }
        }}
        setCommit={() => {
          if (openModal === 'add') {
            addAttribute.mutate()
            addAttribute.reset()
          } else {
            editAttribute.mutate()
            editAttribute.reset()
            setOpenModal(undefined)
          }
        }}
        loading={addAttribute.isLoading || editAttribute.isLoading}
        error={addAttribute.error || editAttribute.error}
      >
        <PrepareRenderInputs
          fields={attributeColsMod}
          modalType={openModal}
          modalData={modalData}
          setModalData={setModalData}
          modalErrors={modalErrors}
          setModalErrors={setModalErrors}
          generateOptions={SelectAttributeOptions}
        />
      </GenericModal>
      <DeleteAttributeModal
        attributeId={deleteModal}
        setAttributeId={setDeleteAttribute}
      />
      <TableContainer sx={{ height: 400, width: 1 }}>
        <DataGridPro
          hideFooter
          rows={
            phase.attributes ?
              newOrder
                .filter((i) =>
                  phase.attributes.map((a) => a.AttrID).includes(i),
                )
                .map((id) => {
                  const attribute = phase.attributes.find(
                    (attribute) => attribute.AttrID === id,
                  )!
                  return {
                    ...attribute,
                    id: attribute.AttrID,
                    actions: attribute.AttrID,
                    CodeSetCount:
                      attribute.options ? attribute.options.length : '',
                  }
                })
            : []
          }
          columns={[
            ...attributeCols,
            {
              type: 'actions',
              field: 'actions',
              headerName: '',
              width: 160,
              getActions: (params: GridRowParams) => {
                const rowLength = newOrder.filter((i) =>
                  phase.attributes.map((a) => a.AttrID).includes(i),
                ).length
                return [
                  <GridActionsCellItem
                    icon={<ArrowUpward />}
                    onClick={() => handleUp(params.id as number)}
                    label="Up"
                    disabled={
                      newOrder.indexOf(params.row.AttrID) === 0 ||
                      !loginConfig?.Modules?.EditStudy ||
                      study_id === '1'
                    }
                  />,
                  <GridActionsCellItem
                    icon={<ArrowDownward />}
                    onClick={() => handleDown(params.id as number)}
                    label="Down"
                    disabled={
                      newOrder.indexOf(params.row.AttrID) === rowLength - 1 ||
                      !loginConfig?.Modules?.EditStudy ||
                      study_id === '1'
                    }
                  />,
                  <GridActionsCellItem
                    icon={<Edit />}
                    onClick={() => {
                      setModalData(params.row)
                      setOpenModal(scope.edit)
                    }}
                    label="Edit"
                    disabled={
                      !loginConfig?.Modules?.EditStudy || study_id === '1'
                    }
                  />,
                  <GridActionsCellItem
                    icon={<Delete />}
                    onClick={() => setDeleteAttribute(params.id as number)}
                    label="Delete"
                    disabled={
                      !loginConfig?.Modules?.EditStudy || study_id === '1'
                    }
                  />,
                ]
              },
            },
          ]}
          initialState={{
            columns: {
              columnVisibilityModel: {
                AttrID: false,
                SeqNo: false,
                CodeSetID: false,
                options: false,
              },
            },
          }}
          disableColumnMenu
          slots={{
            toolbar: EditToolbar,
          }}
          slotProps={{
            toolbar: {
              add: () => {
                //setting initial default for new attribute
                setModalData({
                  AttrDatatype: 'CHR',
                } as unknown as tsStudyAttribute)
                setOpenModal(scope.add)
              },
              setLoadingDialog: undefined,
              title: 'New Attribute',
            },
          }}
        />
      </TableContainer>
    </>
  )
}

export default AttributesTable
